import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { debounce } from 'lodash';
import * as _ from 'lodash';
import { v4 as uuid } from 'uuid';
import {
  CommonDropdown,
  CommonPasswordField,
  CommonTextField,
  CommonErrorHelperTextV1,
} from 'gemlux-common-components';
import { Button, Grid, Link, styled as materialStyled, Typography } from '@mui/material';
import * as yup from 'yup';
import { validation } from '../../../schema/validation';
import LogoIcon from '../../../assets/img/sidebar/logo.svg';
import CustomCheckbox from '../../CustomCheckbox/CustomCheckbox';
import { Nationality } from '../../../utils/enums/nationality.enum';
import { SubordinateSignUpFormProps } from '../../../pages/Subordinates/SignUp/SignUp';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import { authActions } from '../../../redux/auth/authSlice';
import CustomPhoneNumberInput from '../../V1/CustomPhoneNumberInput/CustomPhoneNumberInput';
import { userActions } from '../../../redux/user/userSlice';
import { GemOrigin } from '../../../utils/enums/gem-origin.enum';

const CustomGridContaier = materialStyled(Grid)({
  borderRadius: '8px',
  border: '1px solid #DCE0E5',
  backgroundColor: '#FFFFFF',
  padding: '20px 24px',
  maxWidth: '537px',
});
const CustomGridItem = materialStyled(Grid)({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '16px',
  rowGap: '8px',
});
const CustomButton = materialStyled(Button)({
  backgroundColor: '#3348FF',
  borderRadius: '8px',
  padding: '12px 8px',
  '&:hover': {
    backgroundColor: '#007AFF',
  },
});

const createDropdownOptions = (enumObj: Record<string, string>) =>
  Object.keys(enumObj).map((key) => ({
    label: enumObj[key],
    value: key,
    key: uuid,
  }));

const intitialValues: SubordinateSignUpFormProps = {
  userName: '',
  firstName: '',
  lastName: '',
  mobile: '',
  nationality: '',
  password: '',
  confirmPassword: '',
  companyName: '',
  country: '',
  role: '',
  website: '',
  isAgreedToTermsAndCondition: false,
};

interface SignUpFormProps {
  error: string | undefined | null;
  handleSubmit: (_data: SubordinateSignUpFormProps) => void;
  isNccUser?: boolean;
}
const SignUpForm = ({ error, handleSubmit, isNccUser }: SignUpFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [customPhoneError, setCustomPhoneError] = useState<string | undefined>();
  const dispatch = useAppDispatch();
  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const designations = useAppSelector((state: RootState) => state.user.designations) || [];
  const isUsernameAlreadyExists = _.get(componentStatus, 'isUsernameAlreadyExists', undefined) as boolean;

  useEffect(() => {
    if (designations.length === 0) {
      dispatch(userActions.getDesignationsAsync());
    }
  }, []);

  const roleOptions = designations.map((item) => ({
    label: item.designation,
    value: item.designation,
  }));

  const checkUsernameTakenDebounce = useCallback(
    debounce((input: string, isUsername: boolean) => {
      dispatch(authActions.checkUsernameOrEmailTakenAsync({ input, isUsername }));
    }, 1000),
    [],
  );
  const handleResetState = () => {
    setShowPassword(false);
    setShowConfirmPassword(false);
  };
  const formik = useFormik({
    initialValues: intitialValues,
    validationSchema: yup.object(
      isNccUser
        ? {
            userName: validation.username,
            firstName: validation.firstName,
            lastName: validation.familyName,
            mobile: validation.phoneNumber,
            nationality: validation.country,
            password: validation.password,
            confirmPassword: validation.confirmPassword,
            companyName: validation.companyName,
            country: validation.country,
            role: validation.role,
            website: validation.companyWebsite,
            isAgreedToTermsAndCondition: validation.isAgreedToTermsAndCondition,
          }
        : {
            userName: validation.username,
            firstName: validation.firstName,
            lastName: validation.familyName,
            mobile: validation.phoneNumber,
            nationality: validation.country,
            password: validation.password,
            confirmPassword: validation.confirmPassword,
            isAgreedToTermsAndCondition: validation.isAgreedToTermsAndCondition,
          },
    ),
    onSubmit: (data: SubordinateSignUpFormProps) => {
      handleSubmit(data);
      handleResetState();
      formik.resetForm();
    },
    enableReinitialize: true,
  });

  const handlePhoneNumberChange = (pNumber: string) => {
    formik.setFieldValue('mobile', pNumber);
  };

  const handleErrorChange = (err: string | undefined) => {
    setCustomPhoneError(err);
  };

  useEffect(() => {
    if (formik.values.userName && !formik.errors.userName) {
      checkUsernameTakenDebounce(formik.values.userName, true);
    }
    return () => {
      checkUsernameTakenDebounce.cancel();
      dispatch(authActions.setComponentStatus({ isUsernameAlreadyExists: undefined }));
    };
  }, [formik.values.userName, formik.errors.userName, checkUsernameTakenDebounce]);

  return (
    <CustomGridContaier aria-label="subordinateSignUpForm">
      <Grid
        item
        xs={12}
        sm={12}
        justifyContent="center"
        display="flex"
        alignItems="center"
        flexDirection="column"
        rowGap="40px"
      >
        <Grid component="img" src={LogoIcon} height="32px" />
        <Typography variant="textH5">Please enter your personal credentials</Typography>
      </Grid>

      <Grid container justifyContent="space-between" spacing="20px" pt="20px">
        <CustomGridItem item xs={12} sm={6}>
          <Typography variant="textBase500">Username</Typography>
          <CommonTextField
            type="text"
            name="userName"
            aria-label="userName"
            value={formik.values.userName}
            onBlur={formik.handleBlur}
            variant="outlined"
            size="small"
            onChange={formik.handleChange}
            error={(formik.touched.userName && Boolean(formik.errors.userName)) || isUsernameAlreadyExists}
          />
          {formik.touched.userName && formik.errors.userName && (
            <CommonErrorHelperTextV1 errorText={formik.errors.userName ?? ''} />
          )}
          {isUsernameAlreadyExists && <CommonErrorHelperTextV1 errorText="Username already exists!" />}
        </CustomGridItem>

        <CustomGridItem item xs={12} sm={6} />

        <CustomGridItem item xs={12} sm={6}>
          <Typography variant="textBase500">First Name</Typography>{' '}
          <CommonTextField
            type="text"
            name="firstName"
            aria-label="firstName"
            value={formik.values.firstName}
            onBlur={formik.handleBlur}
            variant="outlined"
            size="small"
            onChange={formik.handleChange}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          />
          {formik.touched.firstName && formik.errors.firstName && (
            <CommonErrorHelperTextV1 errorText={formik.errors.firstName ?? ''} />
          )}
        </CustomGridItem>
        <CustomGridItem item xs={12} sm={6}>
          <Typography variant="textBase500">Last Name</Typography>{' '}
          <CommonTextField
            type="text"
            name="lastName"
            aria-label="lastName"
            value={formik.values.lastName}
            onBlur={formik.handleBlur}
            variant="outlined"
            size="small"
            onChange={formik.handleChange}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          />
          {formik.touched.lastName && formik.errors.lastName && (
            <CommonErrorHelperTextV1 errorText={formik.errors.lastName ?? ''} />
          )}
        </CustomGridItem>
        <CustomGridItem item xs={12} sm={6}>
          <Typography variant="textBase500">Mobile</Typography>{' '}
          <CustomPhoneNumberInput
            handleChange={handlePhoneNumberChange}
            onBlur={formik.handleBlur}
            name="mobile"
            value={formik.values.mobile}
            error={formik.touched.mobile || customPhoneError ? formik.errors.mobile || customPhoneError : undefined}
            onErrorChange={handleErrorChange}
          />
        </CustomGridItem>
        <CustomGridItem item xs={12} sm={6}>
          <Typography variant="textBase500">Nationality</Typography>{' '}
          <CommonDropdown
            options={createDropdownOptions(Nationality)}
            ariaLabelledBy="nationality"
            name="nationality"
            onChange={formik.handleChange}
            value={formik.values.nationality}
            onBlur={formik.handleBlur}
            error={formik.touched.nationality && formik.errors.nationality ? '' : undefined}
          />
          {formik.touched.nationality && formik.errors.nationality && (
            <CommonErrorHelperTextV1 errorText={formik.errors.nationality} />
          )}
        </CustomGridItem>
        {isNccUser && (
          <CustomGridItem item xs={12} sm={6}>
            <Typography variant="textBase500">Company Name</Typography>{' '}
            <CommonTextField
              type="text"
              name="companyName"
              aria-label="companyName"
              value={formik.values.companyName}
              onBlur={formik.handleBlur}
              variant="outlined"
              size="small"
              onChange={formik.handleChange}
              error={formik.touched.companyName && Boolean(formik.errors.companyName)}
            />
            {formik.touched.companyName && formik.errors.companyName && (
              <CommonErrorHelperTextV1 errorText={formik.errors.companyName} />
            )}
          </CustomGridItem>
        )}

        {isNccUser && (
          <CustomGridItem item xs={12} sm={6}>
            <Typography variant="textBase500">Country</Typography>{' '}
            <CommonDropdown
              options={createDropdownOptions(GemOrigin)}
              ariaLabelledBy="country"
              name="country"
              onChange={formik.handleChange}
              value={formik.values.country || ''}
              onBlur={formik.handleBlur}
              error={formik.touched.country && formik.errors.country ? '' : undefined}
            />
            {formik.touched.country && formik.errors.country && (
              <CommonErrorHelperTextV1 errorText={formik.errors.country} />
            )}
          </CustomGridItem>
        )}

        {isNccUser && (
          <CustomGridItem item xs={12} sm={6}>
            <Typography variant="textBase500">Your Role in the Company</Typography>
            <CommonDropdown
              options={roleOptions}
              ariaLabelledBy="role"
              name="role"
              onChange={formik.handleChange}
              value={formik.values.role || ''}
              onBlur={formik.handleBlur}
              error={formik.touched.role && formik.errors.role ? '' : undefined}
            />
            {formik.touched.role && formik.errors.role && <CommonErrorHelperTextV1 errorText={formik.errors.role} />}
          </CustomGridItem>
        )}

        {isNccUser && (
          <CustomGridItem item xs={12} sm={6}>
            <Typography variant="textBase500">Company Website</Typography>{' '}
            <CommonTextField
              type="text"
              name="website"
              aria-label="website"
              value={formik.values.website}
              onBlur={formik.handleBlur}
              variant="outlined"
              size="small"
              onChange={formik.handleChange}
              error={formik.touched.website && Boolean(formik.errors.website)}
            />
            {formik.touched.website && formik.errors.website && (
              <CommonErrorHelperTextV1 errorText={formik.errors.website} />
            )}
          </CustomGridItem>
        )}

        <CustomGridItem item xs={12} sm={6}>
          <Typography variant="textBase500">Password</Typography>{' '}
          <CommonPasswordField
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.errors.password && formik.errors.password}
            id="password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            autoComplete="new-password"
          />
        </CustomGridItem>
        <CustomGridItem item xs={12} sm={6}>
          <Typography variant="textBase500">Confirm Password</Typography>
          <CommonPasswordField
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
                ? formik.errors.confirmPassword
                : undefined
            }
            id="confirmPassword"
            name="confirmPassword"
            autoComplete="new-password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            showPassword={showConfirmPassword}
            setShowPassword={setShowConfirmPassword}
          />
        </CustomGridItem>
      </Grid>

      <Grid
        item
        xs={12}
        sm={12}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        pt="20px"
      >
        <CustomCheckbox
          checked={formik.values.isAgreedToTermsAndCondition}
          onClick={() =>
            formik.setFieldValue('isAgreedToTermsAndCondition', !formik.values.isAgreedToTermsAndCondition)
          }
        />
        <Typography variant="textSM">
          I confirm that I have read and agree to{' '}
          <Link href="/terms&conditions" color="#3348FF" underline="none" target="_blank" rel="noopener noreferrer">
            Porolis’ Terms and Condition of Use
          </Link>
        </Typography>
      </Grid>
      {formik.touched.isAgreedToTermsAndCondition && formik.errors.isAgreedToTermsAndCondition && (
        <CommonErrorHelperTextV1 errorText={formik.errors.isAgreedToTermsAndCondition} />
      )}

      <Grid item justifyContent="center" display="flex" alignItems="center" p="20px">
        <CustomButton variant="contained" onClick={() => formik.handleSubmit()}>
          <Typography variant="textBase500">Create Account </Typography>
        </CustomButton>
      </Grid>

      {error && (
        <Grid item justifyContent="center" display="flex" alignItems="center" p="20px">
          <Typography variant="textBase500" color="error" textAlign="center">
            {_.capitalize(error)}
          </Typography>
        </Grid>
      )}
    </CustomGridContaier>
  );
};

export default SignUpForm;
